// ExpressionTreeRenderer — recursively renders an Include/Exclude expression
// tree as a vertical stack of variable cards and groups, with ConnectorPills
// between siblings reflecting the parent group's operator.
//
// PR 2b-2 introduced the recursive walker with clickable pills.
// PR 2b-3 turns each leaf into a drag source/drop target and each group
// container into a drop target for drag-into-group gestures.
//
// In normal usage the tree is a flat root AND with leaves; nested groups
// appear when the user creates one via drag-to-group, or when loading a
// saved audience that had multiple filters on the same key (the back-compat
// adapter renders that as a per-key OR sub-tree).

import { Fragment } from 'react';
import { useDrop } from 'react-dnd';
import {
  ADD_LEAF_TO_GROUP,
  ADD_MY_DATA_TO_GROUP,
  MOVE_LEAF_INTO_GROUP,
  TOGGLE_OPERATOR,
  TYPES,
  UNGROUP,
} from '../../reducers/builderReducer';
import { canAddAttribute } from '../../helpers/dataDictionary';
import {
  getZoneVariables,
  isGroup,
  isLeaf,
  isMembershipLeaf,
  isModelLeaf,
  membershipLeafId,
} from '../../helpers/expressionTree';
import type {
  ExpressionGroup,
  ExpressionNode,
} from '../../types/dataDictionary';
import ConnectorPill from './ConnectorPill';
import GroupBracket from './GroupBracket';
import MembershipCard from './MembershipCard';
import ModelCard from './ModelCard';
import VariableCard from './VariableCard';


const { VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF } = TYPES;

export type ExpressionTreeRendererProps = {
  tree: ExpressionNode | null;
  zoneType: 'include' | 'exclude';
  builderState: any;
  builderDispatch: (action: { type: string; payload?: any }) => void;
};

type LeafDragItem = { nodeId: string; zoneType: 'include' | 'exclude' };
type DictDragItem = { id: string; name: string; allowedZoneTypes?: string };

export default function ExpressionTreeRenderer({
  tree,
  zoneType,
  builderState,
  builderDispatch,
}: ExpressionTreeRendererProps) {
  if (!tree) return null;

  if (isLeaf(tree)) {
    // Single-leaf root — no operator to show. Dispatch by leaf kind.
    // ATAD-146: model leaves are membership leaves too, so check them first
    // — a MembershipCard would drop the required score range.
    if (isModelLeaf(tree)) {
      return (
        <ModelCard
          leaf={tree}
          zoneType={zoneType}
          builderState={builderState}
          builderDispatch={builderDispatch}
        />
      );
    }
    if (isMembershipLeaf(tree)) {
      return (
        <MembershipCard
          leaf={tree}
          zoneType={zoneType}
          builderState={builderState}
          builderDispatch={builderDispatch}
        />
      );
    }
    return (
      <VariableCard
        variable={tree.variable}
        nodeId={tree.nodeId}
        zoneType={zoneType}
        builderState={builderState}
        builderDispatch={builderDispatch}
      />
    );
  }

  // Root group: render children stacked, with a between-sibling pill at each
  // gap. The pill's operator IS the group's operator; clicking any one
  // toggles the whole group. Root depth = 0 for the depth-color scheme.
  return (
    <RootGroup
      group={tree}
      depth={0}
      zoneType={zoneType}
      builderState={builderState}
      builderDispatch={builderDispatch}
    />
  );
}

// ---------------------------------------------------------------------------
// Root group renderer — no outer border, top-level pills are 'outer' size.
// ---------------------------------------------------------------------------

function RootGroup({
  group,
  depth,
  zoneType,
  builderState,
  builderDispatch,
}: {
  group: ExpressionGroup;
  depth: number;
  zoneType: 'include' | 'exclude';
  builderState: ExpressionTreeRendererProps['builderState'];
  builderDispatch: ExpressionTreeRendererProps['builderDispatch'];
}) {
  const onToggle = () =>
    builderDispatch({
      type: TOGGLE_OPERATOR,
      payload: { nodeId: group.nodeId, zoneType },
    });

  return (
    <div className="flex flex-col">
      {group.children.map((child, idx) => (
        <Fragment key={child.nodeId}>
          {idx > 0 && (
            <div className="py-2">
              <ConnectorPill
                op={group.type}
                onToggle={onToggle}
                size="outer"
                depth={depth}
              />
            </div>
          )}
          {renderChild(child, depth, zoneType, builderState, builderDispatch)}
        </Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nested group renderer — bordered container, header pill + sibling pills.
// ---------------------------------------------------------------------------

function NestedGroup({
  group,
  depth,
  zoneType,
  builderState,
  builderDispatch,
}: {
  group: ExpressionGroup;
  depth: number;
  zoneType: 'include' | 'exclude';
  builderState: ExpressionTreeRendererProps['builderState'];
  builderDispatch: ExpressionTreeRendererProps['builderDispatch'];
}) {
  const onToggle = () =>
    builderDispatch({
      type: TOGGLE_OPERATOR,
      payload: { nodeId: group.nodeId, zoneType },
    });

  const onUngroup = () =>
    builderDispatch({
      type: UNGROUP,
      payload: { nodeId: group.nodeId, zoneType },
    });

  // Drop target on the group container (NOT shallow — anything dropped
  // anywhere in the bordered area appends to this group, including drops
  // that don't hit a child card directly). Card-on-card drops still take
  // precedence because each VariableCard / MembershipCard is a shallow
  // drop target.
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF],
      drop: (item: any, monitor) => {
        if (monitor.didDrop()) return; // a child card already handled it
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          builderDispatch({
            type: ADD_LEAF_TO_GROUP,
            payload: { id: item.id, groupNodeId: group.nodeId, zoneType },
          });
          return;
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          builderDispatch({
            type: ADD_MY_DATA_TO_GROUP,
            payload: {
              id: item.id,
              name: item.name,
              type: item.resourceType,
              groupNodeId: group.nodeId,
              zoneType,
            },
          });
          return;
        }
        if (dragType === EXPRESSION_LEAF) {
          builderDispatch({
            type: MOVE_LEAF_INTO_GROUP,
            payload: {
              sourceNodeId: item.nodeId,
              groupNodeId: group.nodeId,
              zoneType,
            },
          });
          return;
        }
      },
      canDrop: (item: any, monitor) => {
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          if (
            item.allowedZoneTypes &&
            !item.allowedZoneTypes.includes(zoneType)
          ) {
            return false;
          }
          const sameZone = getZoneVariables(builderState, zoneType);
          const otherZone = getZoneVariables(
            builderState,
            zoneType === 'include' ? 'exclude' : 'include',
          );
          return (
            canAddAttribute(sameZone, item.id) &&
            canAddAttribute(otherZone, item.id)
          );
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          // Don't append a duplicate of an audience/file/ZIP/model file
          // already in this group. File, ZIP and model leaves all carry
          // fileId, so match kind.
          return !group.children.some(
            (c) =>
              isLeaf(c) &&
              isMembershipLeaf(c) &&
              c.kind === item.resourceType &&
              membershipLeafId(c) === item.id,
          );
        }
        if (dragType === EXPRESSION_LEAF) {
          if (item.zoneType !== zoneType) return false;
          if (group.children.some((c) => c.nodeId === item.nodeId)) {
            return false;
          }
          return true;
        }
        return false;
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver({ shallow: false }),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [group.nodeId, zoneType, builderState, group.children],
  );

  const isHighlighted = isOver && canDrop;

  // PR 2b-5 layout (matches Screen 4 of the mockup): two-column flex.
  //   LEFT  — bracket SVG (full-height) with a vertical AND/OR pill
  //           positioned on the bracket vertically centered.
  //   RIGHT — group's children stacked, no inner sibling pills (the
  //           single vertical pill on the bracket represents the operator
  //           for the whole group).
  return (
    <div
      ref={drop}
      data-tour="group-bracket"
      className={[
        'rounded-lg p-3 bg-white/60 relative border transition-colors',
        isHighlighted
          ? 'border-2 border-dashed border-teal-500'
          : 'border-gray-300',
      ].join(' ')}
    >
      <div className="flex justify-end mb-1">
        <button
          type="button"
          onClick={onUngroup}
          aria-label="Ungroup"
          data-tour="ungroup-button"
          title="Remove the group; children move up to the parent."
          className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold text-[#2d9cad] bg-[#eef8f9] hover:bg-[#d8eff2] rounded-full transition-colors border border-[#2d9cad]/20"
        >
          <span>Ungroup</span>
        </button>
      </div>
      <div className="flex items-stretch">
        {/* Bracket + vertical pill column. */}
        <div className="relative flex-shrink-0 mr-3 flex items-center">
          <GroupBracket />
          {/* Pill is absolutely centered over the bracket spine. */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full">
            <ConnectorPill
              op={group.type}
              onToggle={onToggle}
              size="inner"
              orientation="vertical"
              depth={depth}
            />
          </div>
        </div>
        {/* Children column. */}
        <div className="flex flex-col flex-grow space-y-2">
          {group.children.map((child) => (
            <Fragment key={child.nodeId}>
              {renderChild(child, depth, zoneType, builderState, builderDispatch)}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared dispatch — leaf renders a card, group recurses as a NestedGroup.
// ---------------------------------------------------------------------------

function renderChild(
  child: ExpressionNode,
  parentDepth: number,
  zoneType: 'include' | 'exclude',
  builderState: ExpressionTreeRendererProps['builderState'],
  builderDispatch: ExpressionTreeRendererProps['builderDispatch'],
) {
  if (isLeaf(child)) {
    // PR 2c: dispatch by leaf kind. Membership leaves render as
    // MembershipCard; variable leaves keep VariableCard. ATAD-146: model
    // leaves (membership leaves with a score range) first, as above.
    if (isModelLeaf(child)) {
      return (
        <ModelCard
          leaf={child}
          zoneType={zoneType}
          builderState={builderState}
          builderDispatch={builderDispatch}
        />
      );
    }
    if (isMembershipLeaf(child)) {
      return (
        <MembershipCard
          leaf={child}
          zoneType={zoneType}
          builderState={builderState}
          builderDispatch={builderDispatch}
        />
      );
    }
    return (
      <VariableCard
        variable={child.variable}
        nodeId={child.nodeId}
        zoneType={zoneType}
        builderState={builderState}
        builderDispatch={builderDispatch}
      />
    );
  }
  if (isGroup(child)) {
    // Nested group is one level deeper than its parent — drives the
    // depth-based pill color (PR 2b-7).
    return (
      <NestedGroup
        group={child}
        depth={parentDepth + 1}
        zoneType={zoneType}
        builderState={builderState}
        builderDispatch={builderDispatch}
      />
    );
  }
  return null;
}
